<template>
  <header
    class="app-header"
    :class="{
      'app-header--scrolled': isScrolled,
      'app-header--hidden': isMobile && !isHeaderVisible,
      'app-header--mobile': isMobile,
      'app-header--search-tabs-expanded':
        isSearchRoute && searchShowCategoryTabs,
    }"
  >
    <div
      class="app-header__inner"
      :class="{
        'app-header__inner--search-tab-wrap':
          isSearchRoute && searchShowCategoryTabs,
      }"
    >
      <!-- Menu button (mobile/tablet only; desktop has persistent left rail) -->
      <button
        v-if="
          !lgAndUp &&
          !(isGroupFeedRoute && currentGroupFeed) &&
          !usesCompactHeaderLayout
        "
        class="app-header__menu-btn"
        aria-label="Menu"
        @click="$emit('toggle-drawer')"
      >
        <span class="app-header__menu-logo-wrap" v-html="gunclubLogoSvg" />
      </button>

      <!-- Search page: top row = shell (back + search) + actions; tabs are siblings below (full header width) -->
      <div v-if="isSearchRoute" class="app-header__search-page-shell">
        <div class="app-header__search-page-bar">
          <button
            type="button"
            class="app-header__search-page-back"
            aria-label="Back"
            @click="router.back()"
          >
            <v-icon size="24">mdi-arrow-left</v-icon>
          </button>
          <SearchPageHeaderInput />
        </div>
      </div>

      <!-- Discover page: back + title + tabs (synced with route.query.tab; same pattern as search) -->
      <div v-else-if="isDiscoverRoute" class="app-header__discover-page-bar">
        <button
          type="button"
          class="app-header__discover-back"
          aria-label="Back"
          @click="router.back()"
        >
          <v-icon size="24">mdi-arrow-left</v-icon>
        </button>
        <h1 class="app-header__discover-title">Discover</h1>
        <div class="app-header__discover-toggle-wrap">
          <v-btn-toggle
            v-model="discoverTabModel"
            mandatory
            density="compact"
            variant="outlined"
            divided
            class="app-header__discover-toggle"
          >
            <v-btn value="trending" size="small">
              <span class="app-header__discover-btn-inner">
                <v-icon size="18">mdi-fire</v-icon>
                <span v-if="smAndUp">Trending</span>
              </span>
            </v-btn>
            <v-btn value="hashtags" size="small">
              <span class="app-header__discover-btn-inner">
                <v-icon size="18">mdi-pound</v-icon>
                <span v-if="smAndUp">Hashtags</span>
              </span>
            </v-btn>
            <v-btn value="follow" size="small">
              <span class="app-header__discover-btn-inner">
                <v-icon size="18">mdi-account-plus-outline</v-icon>
                <span v-if="smAndUp">Who to follow</span>
              </span>
            </v-btn>
          </v-btn-toggle>
        </div>
      </div>

      <!-- Communities list: back + title (tabs live in GroupsView chrome) -->
      <div v-else-if="isGroupsRoute" class="app-header__groups-page-bar">
        <button
          type="button"
          class="app-header__groups-back"
          aria-label="Back"
          @click="goBack"
        >
          <v-icon size="24">mdi-arrow-left</v-icon>
        </button>
        <h1 class="app-header__groups-title">Communities</h1>
      </div>

      <!-- Profile: first reel slide = title row; past first slide = group-style identity chip -->
      <div v-else-if="isProfileRoute" class="app-header__profile-page-bar">
        <button
          type="button"
          class="app-header__profile-back"
          aria-label="Back"
          @click="goBack"
        >
          <v-icon size="24">mdi-arrow-left</v-icon>
        </button>
        <!-- Single-post reel, slide index >= 1: same chrome as group feed compact bar -->
        <div
          v-if="profileReelShowCompactIdentity"
          class="app-header__group-feed-identity"
        >
          <div
            class="app-header__group-feed-identity-bg"
            :style="profileReelHeaderCoverStyle"
          >
            <div class="app-header__group-feed-identity-overlay" />
          </div>
          <div class="app-header__group-feed-identity-row">
            <div class="app-header__group-feed-identity-avatar">
              <img
                v-if="profileHeaderAvatarSrc"
                :src="profileHeaderAvatarSrc"
                :alt="profileHeaderTitle"
                referrerpolicy="no-referrer"
              />
              <span v-else class="app-header__profile-identity-initial">{{
                profileHeaderInitial
              }}</span>
            </div>
            <span class="app-header__group-feed-identity-name">{{
              profileHeaderTitle
            }}</span>
          </div>
        </div>
        <h1 v-else class="app-header__profile-title">Profile</h1>
        <div class="app-header__profile-actions">
          <button
            v-if="profileHeaderIsOwn"
            type="button"
            class="app-header__profile-settings-btn"
            aria-label="Settings"
            @click="router.push('/settings')"
          >
            <v-icon size="22">mdi-cog</v-icon>
          </button>
        </div>
      </div>

      <!-- Subpages: back + title from route.meta.appHeader (see useResolvedAppHeaderSubpage) -->
      <div v-if="appHeaderSubpage" class="app-header__settings-page-bar">
        <button
          v-if="appHeaderSubpage.showBack"
          type="button"
          class="app-header__settings-page-back"
          aria-label="Back"
          @click="goBack"
        >
          <v-icon size="24">mdi-arrow-left</v-icon>
        </button>
        <span class="app-header__settings-page-title">{{
          appHeaderSubpage.title
        }}</span>
      </div>

      <!-- Logo (hidden in playlist edit mode; hidden on mobile, shown in bottom bar instead; hidden on search/settings) -->
      <component
        v-if="!isEditingPlaylist && !isMobile && !usesCompactHeaderLayout"
        :is="isExternalLogo ? 'a' : 'router-link'"
        :to="isExternalLogo ? undefined : '/'"
        :href="isExternalLogo ? logoHref : undefined"
        :target="isExternalLogo ? '_blank' : undefined"
        :rel="isExternalLogo ? 'noopener noreferrer' : undefined"
        class="app-header__logo"
      >
        <!-- Custom site logo -->
        <template v-if="siteLogo">
          <img :src="siteLogo" alt="L" class="app-header__logo-img" />
        </template>
        <!-- Default logo -->
        <template v-else>
          <div class="app-header__logo-icon">
            <v-icon icon="mdi-play-box" size="24" />
          </div>
          <span class="app-header__logo-text">
            <span class="app-header__logo-cc">SOCIAL</span>
            <span class="app-header__logo-demo">!</span>
          </span>
        </template>
      </component>

      <!-- Group feed bar: minimal (on first slide) or full (avatar + name) -->
      <div
        v-if="isGroupFeedRoute && currentGroupFeed"
        class="app-header__group-feed-bar"
      >
        <button
          type="button"
          class="app-header__group-feed-back"
          aria-label="Back"
          @click="router.back()"
        >
          <v-icon size="24">mdi-arrow-left</v-icon>
        </button>
        <!-- Minimal: static label — left-aligned like ProfileScrollAppBar `.toolbar-title` ("Profile") -->
        <div
          v-if="!currentGroupFeedShowCompact"
          class="app-header__group-feed-title"
        >
          Community
        </div>
        <!-- Compact: gradient card + overlay + avatar ring — matches ProfileScrollAppBar toolbar -->
        <div v-else class="app-header__group-feed-identity">
          <div
            class="app-header__group-feed-identity-bg"
            :style="groupFeedHeaderCoverStyle"
          >
            <div class="app-header__group-feed-identity-overlay" />
          </div>
          <div class="app-header__group-feed-identity-row">
            <div class="app-header__group-feed-identity-avatar">
              <img
                v-if="currentGroupFeed.avatar"
                :src="currentGroupFeed.avatar"
                :alt="currentGroupFeed.name"
                referrerpolicy="no-referrer"
              />
              <v-icon v-else size="20">mdi-account-group</v-icon>
            </div>
            <span class="app-header__group-feed-identity-name">{{
              currentGroupFeed.name
            }}</span>
          </div>
        </div>
      </div>

      <!-- Timeline Filter Bar (single-post mode only, centered) -->
      <div
        v-else-if="isTimelineRoute && feedViewMode === 'single-post'"
        class="app-header__timeline-filter"
      >
        <!-- All / Following toggle -->
        <div class="app-header__toggle-group">
          <button
            class="app-header__toggle-btn"
            :class="{ 'app-header__toggle-btn--active': !followingEnabled }"
            @click="setFollowingMode(false)"
          >
            All
          </button>
          <v-tooltip
            text="Log in to see your stuff"
            location="bottom"
            :disabled="isAuthenticated"
          >
            <template #activator="{ props }">
              <button
                v-bind="props"
                class="app-header__toggle-btn"
                :class="{
                  'app-header__toggle-btn--active': followingEnabled,
                  'app-header__toggle-btn--disabled': !isAuthenticated,
                }"
                :disabled="!isAuthenticated"
                @click="isAuthenticated && setFollowingMode(true)"
              >
                Following
                <v-icon
                  v-if="!isAuthenticated"
                  size="14"
                  class="app-header__lock-icon"
                >
                  mdi-lock
                </v-icon>
              </button>
            </template>
          </v-tooltip>
        </div>

        <!-- Advanced filter menu -->
        <v-menu
          location="bottom end"
          :close-on-content-click="false"
          :z-index="3100"
        >
          <template #activator="{ props }">
            <button
              v-bind="props"
              class="app-header__filter-menu-btn"
              aria-label="More filter options"
            >
              <v-icon size="12">mdi-dots-horizontal</v-icon>
            </button>
          </template>
          <div class="app-header__filter-menu">
            <div class="app-header__filter-menu-header">Filter by type</div>
            <label
              v-for="type in headerTimelineDetailedTypes"
              :key="type.value"
              class="app-header__filter-menu-item"
              :class="{
                'app-header__filter-menu-item--disabled':
                  type.value === 'playlist' && !isAuthenticated,
              }"
            >
              <input
                type="checkbox"
                :checked="
                  timelineFilterState.selectedDetailedTypes.value.includes(
                    type.value,
                  )
                "
                :disabled="type.value === 'playlist' && !isAuthenticated"
                @change="timelineFilterState.toggleDetailedType(type.value)"
              />
              <v-icon size="18">{{ type.icon }}</v-icon>
              <span>{{ type.label }}</span>
            </label>
          </div>
        </v-menu>
      </div>

      <!-- Search bar (desktop; hidden on search/settings — dedicated header UI) -->
      <div
        v-if="!usesCompactHeaderLayout"
        class="app-header__search d-none d-md-flex"
      >
        <div
          class="app-header__search-wrapper"
          :class="{ 'app-header__search-wrapper--focused': isSearchFocused }"
        >
          <v-icon class="app-header__search-icon" size="18">mdi-magnify</v-icon>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            placeholder="Search users, posts, videos..."
            class="app-header__search-input"
            @input="handleSearchInput"
            @keyup.enter="handleSearchEnter"
            @keydown.down.prevent="navigateSuggestion(1)"
            @keydown.up.prevent="navigateSuggestion(-1)"
            @keydown.escape="closeSuggestions"
            @focus="handleSearchFocus"
            @blur="handleSearchBlur"
          />
          <v-progress-circular
            v-if="isLoadingSuggestions"
            indeterminate
            size="16"
            width="2"
            class="app-header__search-spinner"
          />
        </div>

        <!-- Suggestions dropdown -->
        <Transition name="suggestions">
          <div
            v-if="
              showSuggestions &&
              (suggestions.length > 0 || isLoadingSuggestions)
            "
            class="app-header__suggestions"
          >
            <div
              v-if="isLoadingSuggestions && suggestions.length === 0"
              class="app-header__suggestions-loading"
            >
              <v-progress-circular indeterminate size="16" width="2" />
              <span>Searching...</span>
            </div>
            <template v-else>
              <button
                v-for="(suggestion, index) in suggestions"
                :key="suggestion.id"
                class="app-header__suggestion"
                :class="{
                  'app-header__suggestion--active':
                    selectedSuggestionIndex === index,
                }"
                @mousedown.prevent="selectSuggestion(suggestion)"
              >
                <v-icon size="18" class="app-header__suggestion-icon">
                  {{ getSuggestionIcon(suggestion.type) }}
                </v-icon>
                <div class="app-header__suggestion-content">
                  <span class="app-header__suggestion-text">{{
                    suggestion.text
                  }}</span>
                  <span
                    v-if="suggestion.subtext"
                    class="app-header__suggestion-subtext"
                  >
                    {{ suggestion.subtext }}
                  </span>
                </div>
                <span class="app-header__suggestion-type">{{
                  suggestion.type
                }}</span>
              </button>
            </template>
          </div>
        </Transition>
      </div>

      <!-- Actions -->
      <div class="app-header__actions">
        <!-- Mobile: Only show user avatar/login (simplified) -->
        <!-- Desktop: Show all action buttons -->

        <!-- Discover icon (tablet only - not on mobile since we have bottom tab; hidden on search/settings/discover) -->
        <button
          v-if="!isMobile && !usesCompactHeaderLayout"
          class="app-header__action-btn d-lg-none"
          @click="$emit('open-discover')"
          aria-label="Open discover"
        >
          <v-icon size="22">mdi-compass-outline</v-icon>
        </button>

        <!-- Search icon (tablet only - not on mobile since we have bottom tab; hidden on search page) -->
        <button
          v-if="!isMobile && !usesCompactHeaderLayout"
          type="button"
          class="app-header__action-btn d-md-none"
          aria-label="Open search"
          @click="$emit('open-search')"
        >
          <v-icon size="22">mdi-magnify</v-icon>
        </button>

        <!-- Discover: mobile uses bottom tab bar compass (BottomTabBar) -->

        <!-- Acting Context Badge (desktop only) -->
        <ActingContextBanner v-if="isAuthenticated && !isMobile" />

        <!-- Playlist Edit Mode Badge (fills space when active, desktop only) -->
        <PlaylistEditBanner v-if="isAuthenticated && !isMobile" />

        <!-- Queue button (visible on desktop and mobile, hidden in radio mode and social-only sites) -->
        <!-- <button
          v-if="!isRadioStationMode"
          class="app-header__action-btn app-header__queue-btn"
          :class="{ 'app-header__queue-btn--active': queuePanelOpen }"
          @click="toggleQueuePanel"
          aria-label="Toggle queue"
        >
          <v-icon size="22">mdi-playlist-play</v-icon>
          <span v-if="queue.length > 0" class="app-header__queue-badge">
            {{ queue.length }}
          </span>
        </button> -->

        <!-- Notifications dropdown (when authenticated, hidden in playlist edit mode, desktop only - mobile has bottom tab) -->
        <NotificationCenter
          v-if="
            isAuthenticated &&
            !isEditingPlaylist &&
            !isMobile &&
            route.name !== 'notifications'
          "
        />

        <!-- User menu -->
        <v-menu
          v-if="isAuthenticated"
          location="bottom end"
          :offset="8"
          :z-index="3100"
        >
          <template #activator="{ props }">
            <button class="app-header__avatar-btn" v-bind="props">
              <div class="app-header__avatar">
                <v-img
                  v-if="userAvatar"
                  :src="userAvatar"
                  :alt="user?.name"
                  cover
                />
                <span v-else class="app-header__avatar-initial">
                  {{ user?.name?.charAt(0).toUpperCase() || "U" }}
                </span>
              </div>
              <v-icon size="16" class="app-header__avatar-caret"
                >mdi-chevron-down</v-icon
              >
            </button>
          </template>

          <div class="app-header__menu">
            <div class="app-header__menu-user">
              <div class="app-header__menu-avatar">
                <v-img
                  v-if="userAvatar"
                  :src="userAvatar"
                  :alt="user?.name"
                  cover
                />
                <span v-else>{{
                  user?.name?.charAt(0).toUpperCase() || "U"
                }}</span>
              </div>
              <div class="app-header__menu-user-info">
                <span class="app-header__menu-name">{{
                  user?.displayName || user?.name || user?.username || "User"
                }}</span>
                <span class="app-header__menu-handle"
                  >@{{ user?.username || "unknown" }}</span
                >
              </div>
            </div>

            <div class="app-header__menu-divider"></div>

            <button class="app-header__menu-item" @click="goToProfile">
              <v-icon size="18">mdi-account-outline</v-icon>
              <span>Profile</span>
            </button>
            <button class="app-header__menu-item" @click="goToAnalytics">
              <v-icon size="18">mdi-chart-areaspline</v-icon>
              <span>Analytics</span>
            </button>
            <button class="app-header__menu-item" @click="goToBadges">
              <v-icon size="18">mdi-medal-outline</v-icon>
              <span>Badges</span>
            </button>
            <button
              v-if="hasAdministratorBadge"
              class="app-header__menu-item"
              @click="goToAdminDashboard"
            >
              <v-icon size="18">mdi-view-dashboard-outline</v-icon>
              <span>Admin Dashboard</span>
            </button>
            <button class="app-header__menu-item" @click="goToSettings">
              <v-icon size="18">mdi-cog-outline</v-icon>
              <span>Settings</span>
            </button>
            <button class="app-header__menu-item" @click="goToManageUsers">
              <v-icon size="18">mdi-account-group</v-icon>
              <span>Manage Users</span>
            </button>

            <div class="app-header__menu-divider"></div>

            <button
              class="app-header__menu-item app-header__menu-item--danger"
              @click="handleLogout"
            >
              <v-icon size="18">mdi-logout</v-icon>
              <span>Sign out</span>
            </button>
          </div>
        </v-menu>

        <!-- Login: icon like compass on mobile, pill on desktop -->
        <router-link
          v-else-if="route.name !== 'login'"
          to="/login"
          :class="isMobile ? 'app-header__action-btn' : 'app-header__login-btn'"
          :aria-label="isMobile ? 'Sign In' : undefined"
        >
          <v-icon v-if="isMobile" size="22">mdi-login</v-icon>
          <span v-else>Sign In</span>
        </router-link>
      </div>

      <!-- Second row: spans full inner width (not the shell column beside the avatar) -->
      <nav
        v-if="isSearchRoute && searchShowCategoryTabs"
        class="app-header__search-page-tabs"
        aria-label="Search categories"
      >
        <button
          v-for="tab in searchPageTabItems"
          :key="tab.value"
          type="button"
          class="app-header__search-page-tab"
          :class="{
            'app-header__search-page-tab--active':
              searchResultType === tab.value,
          }"
          @click="setSearchResultType(tab.value)"
        >
          {{ tab.label }}
        </button>
      </nav>
    </div>

    <!-- Upload progress indicator (positioned below header) -->
    <Transition name="upload-indicator">
      <div
        v-if="uploadStatus !== 'idle'"
        class="app-header__upload-bar"
        :class="{
          'app-header__upload-bar--uploading': uploadStatus === 'uploading',
          'app-header__upload-bar--success': uploadStatus === 'success',
          'app-header__upload-bar--error': uploadStatus === 'error',
        }"
      >
        <button
          type="button"
          class="app-header__upload-bar-summary"
          :aria-expanded="uploadPanelOpen"
          @click="toggleUploadPanel"
        >
          <div class="app-header__upload-bar-track">
            <div
              v-if="uploadStatus === 'uploading'"
              class="app-header__upload-bar-progress"
              :class="{
                'app-header__upload-bar-progress--indeterminate': !uploadHasPct,
              }"
              :style="
                uploadHasPct
                  ? { width: uploadPctClamped + '%', animation: 'none' }
                  : undefined
              "
            />
          </div>
          <div class="app-header__upload-bar-label">
            <v-icon v-if="uploadStatus === 'success'" size="12"
              >mdi-check</v-icon
            >
            <v-icon v-else-if="uploadStatus === 'error'" size="12"
              >mdi-alert-circle-outline</v-icon
            >
            <span>{{ uploadDisplayMessage }}</span>
            <v-icon
              v-if="uploadJobs.length > 0"
              size="14"
              class="app-header__upload-bar-chevron"
              :class="{
                'app-header__upload-bar-chevron--open': uploadPanelOpen,
              }"
            >
              mdi-chevron-down
            </v-icon>
          </div>
        </button>
        <Transition name="upload-panel">
          <div
            v-if="uploadPanelOpen && uploadJobs.length > 0"
            class="app-header__upload-panel"
          >
            <div
              v-for="job in uploadJobs"
              :key="job.id"
              class="app-header__upload-panel-item"
              :class="{
                'app-header__upload-panel-item--error': job.status === 'error',
                'app-header__upload-panel-item--done':
                  job.status === 'completed',
              }"
            >
              <div class="app-header__upload-panel-row">
                <span
                  class="app-header__upload-panel-name"
                  :title="job.fileName"
                >
                  {{ job.fileName }}
                </span>
                <span class="app-header__upload-panel-pct">
                  <template v-if="job.status === 'queued'">Queued</template>
                  <template v-else-if="job.status === 'completed'"
                    >Done</template
                  >
                  <template v-else-if="job.status === 'error'">{{
                    job.errorMessage || "Failed"
                  }}</template>
                  <template v-else>{{ Math.floor(job.pct) }}%</template>
                </span>
              </div>
              <div class="app-header__upload-panel-track">
                <div
                  class="app-header__upload-panel-track-fill"
                  :style="{
                    width: Math.max(2, Math.min(100, job.pct)) + '%',
                  }"
                />
              </div>
              <div
                class="app-header__upload-panel-row app-header__upload-panel-row--meta"
              >
                <span>
                  {{ formatBytesShort(job.uploadedBytes) }} /
                  {{ formatBytesShort(job.totalBytes) }}
                </span>
                <button
                  v-if="job.status === 'uploading' || job.status === 'queued'"
                  type="button"
                  class="app-header__upload-panel-cancel"
                  @click.stop="cancelUploadJob(job.id)"
                >
                  Cancel
                </button>
                <button
                  v-else-if="
                    job.status === 'completed' || job.status === 'error'
                  "
                  type="button"
                  class="app-header__upload-panel-cancel"
                  @click.stop="removeUploadJob(job.id)"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </header>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  inject,
  watch,
  type Ref,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { useAuth } from "@/composables/useAuth";
import { useSdk } from "@/composables/useSdk";
import { useAppSettings } from "@/composables/useAppSettings";
import { storeToRefs } from "pinia";
import { useUiStore, type SearchResultTab } from "@/stores/ui";
import { useScrollDirection } from "@/composables/useScrollDirection";
import { useUploadProgress } from "@/composables/useUploadProgress";
import { useUploadQueueStore } from "@/stores/uploadQueue";
import { useProcessingPostsStore } from "@/stores/processingPosts";
import { debounce } from "@/utils/debounce";
import ActingContextBanner from "@/components/ActingContextBanner.vue";
import SearchPageHeaderInput from "@/components/search/SearchPageHeaderInput.vue";
import PlaylistEditBanner from "@/components/playlists/PlaylistEditBanner.vue";
import { usePlaylistEditMode } from "@/composables/usePlaylistEditMode";
import { useAudioPlayer } from "@/composables/useAudioPlayer";
import { useTimelineFilter } from "@/composables/useTimelineFilter";
import { useResolvedAppHeaderSubpage } from "@/composables/useAppHeaderSubpage";
import { useUserProfile } from "@/queries/useProfileQueries";
import type { UserProfile } from "@cc-consulting-nv/ccsdk";
import SinglePostFeedView from "../feed/reel/SinglePostFeedView.vue";
import gunclubLogoRaw from "@/assets/gunclub-logo.svg?raw";

const gunclubLogoSvg = gunclubLogoRaw.trim();
const NotificationCenter = defineAsyncComponent(
  () => import("@/components/notifications/NotificationCenter.vue"),
);

// Search suggestion types
interface SearchSuggestion {
  id: string;
  type: "user" | "audio" | "hashtag" | "query";
  text: string;
  subtext?: string;
  data?:
    | UserProfile
    | { name: string }
    | { ulid: string; title: string; username: string };
}

const router = useRouter();
const route = useRoute();
const { mobile: isMobile, smAndUp, lgAndUp } = useDisplay();
const { user, isAuthenticated, logout } = useAuth();
const { sdk } = useSdk();
const { siteLogo, siteWebsite, isMusicMode, timelinePlaylistFeedEnabled } =
  useAppSettings();
const uiStore = useUiStore();
const { searchShowCategoryTabs } = storeToRefs(uiStore);
const { isEditingPlaylist } = usePlaylistEditMode();
const { queue, queuePanelOpen, toggleQueuePanel, isRadioStationMode } =
  useAudioPlayer();
const { status: legacyUploadStatus, message: legacyUploadMessage } =
  useUploadProgress();
const uploadQueue = useUploadQueueStore();
const processingPosts = useProcessingPostsStore();
const {
  hasActiveUpload,
  overallPct,
  jobs: uploadJobs,
} = storeToRefs(uploadQueue);

function formatBytesShort(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)}GB`;
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(0)}MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(0)}KB`;
  }
  return `${bytes}B`;
}

const errorJob = computed(() => {
  const errored = uploadJobs.value.filter((j) => j.status === "error");
  return errored[errored.length - 1] ?? null;
});

const activeUploadJobs = computed(() =>
  uploadJobs.value.filter(
    (j) => j.status === "uploading" || j.status === "queued",
  ),
);

const uploadStatus = computed<
  "idle" | "uploading" | "processing" | "success" | "error"
>(() => {
  if (hasActiveUpload.value) return "uploading";
  if (errorJob.value && legacyUploadStatus.value !== "success") return "error";
  if (legacyUploadStatus.value !== "idle") return legacyUploadStatus.value;
  if (processingPosts.hasProcessing) return "processing";
  return "idle";
});

const uploadHasPct = computed(() => {
  return activeUploadJobs.value.some((j) => j.totalBytes > 0);
});

const uploadPctClamped = computed(() => {
  const pct = overallPct.value;
  if (!Number.isFinite(pct)) return 0;
  return Math.max(2, Math.min(100, pct));
});

const uploadDisplayMessage = computed(() => {
  const active = activeUploadJobs.value;
  if (active.length > 1) {
    const totalBytes = active.reduce((sum, j) => sum + j.totalBytes, 0);
    const uploadedBytes = active.reduce((sum, j) => sum + j.uploadedBytes, 0);
    const pct =
      totalBytes > 0 ? Math.floor((uploadedBytes / totalBytes) * 100) : 0;
    return `${active.length} uploads • ${pct}% • ${formatBytesShort(uploadedBytes)}/${formatBytesShort(totalBytes)}`;
  }
  if (active.length === 1) {
    const job = active[0];
    const pctStr = `${Math.floor(job.pct)}%`;
    const bytesStr = `${formatBytesShort(job.uploadedBytes)}/${formatBytesShort(
      job.totalBytes,
    )}`;
    const label = job.mediaType === "video" ? "Uploading video" : "Uploading";
    return `${label} ${pctStr} • ${bytesStr}`;
  }
  if (uploadStatus.value === "error" && errorJob.value) {
    return errorJob.value.errorMessage || "Upload failed";
  }
  if (uploadStatus.value === "processing") {
    const n = processingPosts.count;
    return n === 1 ? "Processing 1 post" : `Processing ${n} posts`;
  }
  return legacyUploadMessage.value;
});

const uploadPanelOpen = ref(false);

function toggleUploadPanel(): void {
  if (uploadJobs.value.length === 0) return;
  uploadPanelOpen.value = !uploadPanelOpen.value;
}

function cancelUploadJob(id: string): void {
  void uploadQueue.cancel(id);
}

function removeUploadJob(id: string): void {
  uploadQueue.remove(id);
}

// beforeunload guard while large uploads are running
function beforeUnloadHandler(event: BeforeUnloadEvent): string | undefined {
  if (!hasActiveUpload.value) return undefined;
  event.preventDefault();
  event.returnValue = "Upload in progress — leaving will cancel it.";
  return event.returnValue;
}

onMounted(() => {
  window.addEventListener("beforeunload", beforeUnloadHandler);
});
onUnmounted(() => {
  window.removeEventListener("beforeunload", beforeUnloadHandler);
});

// Feed view mode
const feedViewMode = computed(() => uiStore.feedViewMode);

// Check if current route is timeline
const isTimelineRoute = computed(() => {
  return route.name === "timeline" || route.path.startsWith("/timeline");
});

// Communities list (all tab routes) — not detail / feed / manage
const isGroupsRoute = computed(() => {
  const name = route.name as string | undefined;
  if (
    name === "group-detail" ||
    name === "group-feed" ||
    name === "group-manage"
  ) {
    return false;
  }
  if (name?.startsWith("groups")) return true;
  return (
    route.path === "/communities" ||
    route.path === "/communities/favorites" ||
    route.path === "/communities/mine" ||
    route.path === "/communities/all"
  );
});

// Group feed view: show group avatar + name in header
const isGroupFeedRoute = computed(() => {
  return (
    route.name === "group-feed" || /^\/groups\/[^/]+\/feed$/.test(route.path)
  );
});

// Search page: show search bar in header (back + input synced with route.query.q)
const isSearchRoute = computed(() => route.name === "search");

// Discover hub (/discover): back + title + tabs (synced with route.query.tab)
const isDiscoverRoute = computed(() => route.name === "discover");

// Profile: back + title + trailing actions (mirrors Discover shell)
const isProfileRoute = computed(() => route.name === "profile");

type DiscoverHeaderTab = "trending" | "hashtags" | "follow";

function discoverTabFromQuery(): DiscoverHeaderTab {
  const raw = route.query.tab;
  const t = Array.isArray(raw) ? raw[0] : raw;
  if (t === "hashtags" || t === "follow") return t;
  return "trending";
}

const discoverTabModel = computed({
  get: (): DiscoverHeaderTab => discoverTabFromQuery(),
  set: (tab: DiscoverHeaderTab) => {
    const q = { ...route.query } as Record<
      string,
      string | string[] | undefined
    >;
    if (tab === "trending") {
      delete q.tab;
    } else {
      q.tab = tab;
    }
    void router.replace({ name: "discover", query: q });
  },
});

const appHeaderSubpage = useResolvedAppHeaderSubpage();

const usesCompactHeaderLayout = computed(
  () =>
    isSearchRoute.value ||
    isDiscoverRoute.value ||
    isGroupsRoute.value ||
    isProfileRoute.value ||
    appHeaderSubpage.value != null,
);

const profileRouteUsernameParam = computed(() => {
  if (route.name !== "profile") return "";
  const u = route.params.username;
  return typeof u === "string" ? u : "";
});

const profileResolvedUsername = computed(() => {
  const raw = profileRouteUsernameParam.value;
  if (!raw) return "";
  if (raw === "me") {
    return user.value?.username ?? "";
  }
  return raw;
});

const { user: profileRouteUser } = useUserProfile(profileResolvedUsername);

function profileUserAvatarThumb(p: UserProfile | null | undefined): string {
  if (!p) return "";
  const rec = p as Record<string, unknown>;
  const variants = rec.avatarVariants as
    | { thumbWebp?: string; thumb?: string }
    | null
    | undefined;
  const base = (rec.avatarUrl || rec.avatar) as string | undefined;
  if (variants?.thumbWebp) return variants.thumbWebp;
  if (variants?.thumb) return variants.thumb;
  return base || "";
}

const profileHeaderTitle = computed(() => {
  const u = profileRouteUser.value;
  if (u) {
    return String(
      u.displayName || u.name || u.username || profileResolvedUsername.value,
    );
  }
  const param = profileRouteUsernameParam.value;
  if (param && param !== "me") return `@${param}`;
  if (param === "me" && user.value?.username) return `@${user.value.username}`;
  return "Profile";
});

const profileHeaderAvatarSrc = computed(() =>
  profileUserAvatarThumb(profileRouteUser.value),
);

const profileHeaderInitial = computed(() => {
  const u = profileRouteUser.value;
  const label = String(
    u?.displayName ||
      u?.name ||
      u?.username ||
      profileResolvedUsername.value ||
      "?",
  );
  return label.charAt(0).toUpperCase();
});

const profileHeaderIsOwn = computed(() => {
  if (!isAuthenticated.value || !user.value?.username) return false;
  const resolved = profileResolvedUsername.value;
  if (!resolved) return false;
  return user.value.username.toLowerCase() === resolved.toLowerCase();
});

function profileUserBackgroundCoverUrl(
  p: UserProfile | null | undefined,
): string {
  if (!p) return "";
  const rec = p as Record<string, unknown>;
  const raw = (rec.backgroundImage ||
    rec.background ||
    rec.coverImage ||
    rec.cover_image ||
    rec.banner) as string | undefined;
  const t = raw?.trim();
  return t || "";
}

const profileReelHeaderCoverStyle = computed((): Record<string, string> => {
  const url = profileUserBackgroundCoverUrl(profileRouteUser.value);
  if (!url || (!url.startsWith("https://") && !url.startsWith("http://")))
    return {};
  return { backgroundImage: `url(${CSS.escape(url)})` };
});

const currentProfileFeedShowCompact = computed(
  () => uiStore.currentProfileFeedShowCompact,
);

const profileReelShowCompactIdentity = computed(
  () =>
    currentProfileFeedShowCompact.value && feedViewMode.value === "single-post",
);

function goBack() {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push("/");
  }
}

const searchPageTabItems: { value: SearchResultTab; label: string }[] = [
  { value: "posts", label: "Top" },
  { value: "hashtags", label: "Trending" },
  { value: "communities", label: "Communities" },
  { value: "people", label: "People" },
];

const searchResultType = computed(() => uiStore.searchResultType);

function setSearchResultType(value: SearchResultTab) {
  uiStore.setSearchResultType(value);
}

const currentGroupFeed = computed(() => uiStore.currentGroupFeed);
const currentGroupFeedShowCompact = computed(
  () => uiStore.currentGroupFeedShowCompact,
);

/** Optional cover behind compact group chrome (ProfileScrollAppBar pattern) */
const groupFeedHeaderCoverStyle = computed((): Record<string, string> => {
  const url = currentGroupFeed.value?.background?.trim();
  if (!url || (!url.startsWith("https://") && !url.startsWith("http://")))
    return {};
  return { backgroundImage: `url(${CSS.escape(url)})` };
});

// Following toggle state (syncs with route param)
const followingEnabled = computed(() => {
  return route.params.scope === "following" && isAuthenticated.value;
});

function setFollowingMode(enabled: boolean) {
  const scope = enabled ? "following" : "all";
  router.push({
    name: "timeline",
    params: { scope },
    query: route.query,
  });
}

// Use timeline filter composable (shared state)
// Note: composable must be called unconditionally, but we only use it when on timeline route
const timelineFilterState = useTimelineFilter();

const headerTimelineDetailedTypes = computed(() => {
  const types = timelineFilterState.detailedContentTypes;
  return timelinePlaylistFeedEnabled.value
    ? types
    : types.filter((t) => t.value !== "playlist");
});

// b4e23a3: useVirtualFeed drives header hide on scroll for traditional feeds.
// FeedList syncs into App-provided ref (FeedList is not an ancestor of AppHeader, so provide/inject from FeedList alone never worked).
// Single-post: useVirtualFeed disables that sync (snap scroll is not document scroll) via syncAppHeaderVisibilityFromScroll.
// When override is null (no feed mounted), fall back to useScrollDirection (b725d2e).
const feedHeaderVisibilityOverride = inject<Ref<boolean | null> | undefined>(
  "feedHeaderVisibilityOverride",
  undefined,
);
const {
  isHeaderVisible: scrollDirectionHeaderVisible,
  scrollY: trackedScrollY,
} = useScrollDirection({
  threshold: 15,
});

const isHeaderVisible = computed(() => {
  const o = feedHeaderVisibilityOverride?.value;
  if (o === null || o === undefined) {
    return scrollDirectionHeaderVisible.value;
  }
  return o;
});

const searchInputRef = ref<HTMLInputElement | null>(null);
const searchQuery = ref("");

const isScrolled = ref(false);
const apiNotificationCount = ref(0);

// Check if user has administrator badge
const hasAdministratorBadge = computed(() => {
  return user.value?.badges?.includes("administrator") ?? false;
});

// Search suggestions state
const isSearchFocused = ref(false);
const showSuggestions = ref(false);
const suggestions = ref<SearchSuggestion[]>([]);
const isLoadingSuggestions = ref(false);
const selectedSuggestionIndex = ref(-1);

// Genre pills state (scroll-aware for mobile)
const isGenresVisible = ref(true);
const scrollThreshold = 10;

// Top genres with colors and icons
const topGenres = computed(() => [
  {
    name: "Hip Hop",
    slug: "hip-hop",
    color: "#E91E63",
    icon: "mdi-microphone",
  },
  { name: "Pop", slug: "pop", color: "#9C27B0", icon: "mdi-star" },
  { name: "R&B", slug: "rnb", color: "#673AB7", icon: "mdi-heart" },
  { name: "Rock", slug: "rock", color: "#3F51B5", icon: "mdi-guitar-electric" },
  {
    name: "Electronic",
    slug: "electronic",
    color: "#00BCD4",
    icon: "mdi-sine-wave",
  },
  {
    name: "Country",
    slug: "country",
    color: "#FF9800",
    icon: "mdi-guitar-acoustic",
  },
  { name: "Jazz", slug: "jazz", color: "#795548", icon: "mdi-saxophone" },
  {
    name: "Classical",
    slug: "classical",
    color: "#607D8B",
    icon: "mdi-violin",
  },
]);

// Show genre pills only on music-related routes that don't have their own genre filtering
const showGenrePills = computed(() => {
  const path = route.path;
  // Exclude routes that have their own genre filter UI or don't need genre navigation
  if (path.startsWith("/music/new") || path.startsWith("/music/playlists")) {
    return false;
  }
  return (
    path === "/music" ||
    path.startsWith("/music/") ||
    path === "/popular" ||
    path.startsWith("/popular/") ||
    path === "/liked-songs" ||
    path === "/playlists" ||
    path.startsWith("/playlists/")
  );
});

// Logo link destination - use siteWebsite if available, otherwise home
const logoHref = computed(() => siteWebsite.value || "/");
const isExternalLogo = computed(() => !!siteWebsite.value);

// Notification count from API
// TODO: Add Laravel Echo/Pusher subscription to update this count in real-time
const notificationCount = computed(() => apiNotificationCount.value);

// Fetch notification count from API
async function fetchNotificationCount() {
  if (!isAuthenticated.value) {
    apiNotificationCount.value = 0;
    return;
  }
  try {
    const count = await sdk.getUnreadNotificationCount();
    apiNotificationCount.value = count;
  } catch {
    // Silently fail - notifications are non-critical
    apiNotificationCount.value = 0;
  }
}

// Watch for auth changes to fetch notification count
watch(
  isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      fetchNotificationCount();
    } else {
      apiNotificationCount.value = 0;
    }
  },
  { immediate: true },
);

// Computed avatar that handles different field names from API
const userAvatar = computed(() => {
  if (!user.value) return null;
  const u = user.value as Record<string, unknown>;
  return (u.avatar || u.avatarUrl || u.avatar_url || u.profile_image) as
    | string
    | null;
});

// Fetch suggestions from API
async function fetchSuggestions() {
  const query = searchQuery.value.trim();
  if (query.length < 3) {
    suggestions.value = [];
    return;
  }

  isLoadingSuggestions.value = true;
  const newSuggestions: SearchSuggestion[] = [];

  try {
    // Fetch users, audio, and hashtags in parallel (avoid autocomplete routes for very short queries)
    const [usersResult, audioResult, hashtagsResult] = await Promise.all([
      sdk.searchUsersAutocomplete(query).catch((e) => {
        console.error("User search failed:", e);
        return { data: [] as UserProfile[] };
      }),
      sdk.searchAudio(query).catch((e) => {
        console.error("Audio search failed:", e);
        return {
          data: [] as Array<{ ulid: string; title: string; username: string }>,
        };
      }),
      sdk.searchHashtags(query).catch((e) => {
        console.error("Hashtag search failed:", e);
        return { data: { items: [] as Array<{ name: string }> } };
      }),
    ]);

    console.log("Search results:", {
      usersResult,
      audioResult,
      hashtagsResult,
    });

    // Extract users - handle both { data: [...] } and direct array responses
    const usersData = usersResult?.data;
    const users: UserProfile[] = Array.isArray(usersData) ? usersData : [];
    console.log("Parsed users:", users);

    for (const u of users.slice(0, 3)) {
      newSuggestions.push({
        id: `user-${u.ulid}`,
        type: "user",
        text: u.name || u.displayName || u.username || "Unknown",
        subtext: `@${u.username || "unknown"}`,
        data: u,
      });
    }

    // Extract audio - handle both { data: [...] } and direct array responses
    const audioData = audioResult?.data;
    const audio = Array.isArray(audioData) ? audioData : [];
    console.log("Parsed audio:", audio);

    for (const a of audio.slice(0, 3)) {
      newSuggestions.push({
        id: `audio-${a.ulid}`,
        type: "audio",
        text: a.title,
        subtext: `@${a.username}`,
        data: a,
      });
    }

    // Extract hashtags - handle nested { data: { items: [...] } } structure
    const hashtagItems = hashtagsResult?.data?.items;
    const hashtags = Array.isArray(hashtagItems) ? hashtagItems : [];
    console.log("Parsed hashtags:", hashtags);

    for (const h of hashtags.slice(0, 2)) {
      newSuggestions.push({
        id: `hashtag-${h.name}`,
        type: "hashtag",
        text: `#${h.name}`,
        data: h,
      });
    }

    // Add query suggestion at the beginning
    if (query && !query.startsWith("#") && !query.startsWith("@")) {
      newSuggestions.unshift({
        id: `query-${query}`,
        type: "query",
        text: query,
        subtext: "Search all",
      });
    }

    suggestions.value = newSuggestions;
    console.log(
      "Final suggestions:",
      newSuggestions,
      "showSuggestions:",
      showSuggestions.value,
    );
  } catch (error) {
    console.error("Failed to fetch suggestions:", error);
  } finally {
    isLoadingSuggestions.value = false;
    console.log(
      "Fetch complete. suggestions.length:",
      suggestions.value.length,
      "showSuggestions:",
      showSuggestions.value,
    );
  }
}

// Debounced suggestion fetch
const debouncedFetchSuggestions = debounce(() => {
  fetchSuggestions();
}, 200);

// Handle search input
function handleSearchInput() {
  selectedSuggestionIndex.value = -1;
  if (searchQuery.value.trim().length >= 3) {
    showSuggestions.value = true;
    debouncedFetchSuggestions();
  } else {
    showSuggestions.value = false;
    suggestions.value = [];
  }
}

// Handle search focus
function handleSearchFocus() {
  isSearchFocused.value = true;
  if (searchQuery.value.trim().length >= 3 && suggestions.value.length > 0) {
    showSuggestions.value = true;
  }
}

// Handle search blur
function handleSearchBlur() {
  isSearchFocused.value = false;
  // Delay hiding to allow click events on suggestions
  setTimeout(() => {
    showSuggestions.value = false;
  }, 200);
}

// Handle Enter key
function handleSearchEnter() {
  if (
    selectedSuggestionIndex.value >= 0 &&
    suggestions.value[selectedSuggestionIndex.value]
  ) {
    selectSuggestion(suggestions.value[selectedSuggestionIndex.value]);
  } else if (searchQuery.value.trim()) {
    closeSuggestions();
    router.push({ path: "/search", query: { q: searchQuery.value.trim() } });
  }
}

// Navigate suggestions with keyboard
function navigateSuggestion(direction: number) {
  if (!showSuggestions.value || suggestions.value.length === 0) return;

  const newIndex = selectedSuggestionIndex.value + direction;
  if (newIndex < -1) {
    selectedSuggestionIndex.value = suggestions.value.length - 1;
  } else if (newIndex >= suggestions.value.length) {
    selectedSuggestionIndex.value = -1;
  } else {
    selectedSuggestionIndex.value = newIndex;
  }
}

// Close suggestions
function closeSuggestions() {
  showSuggestions.value = false;
  selectedSuggestionIndex.value = -1;
}

// Select a suggestion
function selectSuggestion(suggestion: SearchSuggestion) {
  closeSuggestions();
  searchQuery.value = "";

  if (suggestion.type === "user") {
    const u = suggestion.data as UserProfile;
    router.push(`/profile/${u.username}`);
    return;
  }

  if (suggestion.type === "audio") {
    const a = suggestion.data as { ulid: string };
    router.push(`/song/${a.ulid}`);
    return;
  }

  if (suggestion.type === "hashtag") {
    router.push({ path: "/search", query: { q: suggestion.text } });
    return;
  }

  // Query type - go to search page
  router.push({ path: "/search", query: { q: suggestion.text } });
}

// Get icon for suggestion type
function getSuggestionIcon(type: SearchSuggestion["type"]): string {
  switch (type) {
    case "user":
      return "mdi-account-outline";
    case "audio":
      return "mdi-music-note";
    case "hashtag":
      return "mdi-pound";
    default:
      return "mdi-magnify";
  }
}

function goToProfile() {
  if (user.value) {
    router.push(`/profile/${user.value.username}`);
  }
}

function goToAnalytics() {
  router.push("/analytics");
}

function goToBadges() {
  router.push("/badges");
}

function goToAdminDashboard() {
  router.push("/admin/dashboard");
}

function goToSettings() {
  if (user.value) {
    router.push(`/profile/${user.value.username}/settings`);
  }
}

function goToManageUsers() {
  router.push("/manage-users");
}

function handleLogout() {
  logout();
}

// Handle "/" hotkey for search (desktop only)
function handleKeydown(event: KeyboardEvent) {
  // Only handle "/" key on desktop
  if (isMobile.value) return;

  // Ignore if user is typing in an input, textarea, or contenteditable
  const target = event.target as HTMLElement;
  if (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable
  ) {
    return;
  }

  // Focus search on "/" key
  if (event.key === "/") {
    event.preventDefault();
    searchInputRef.value?.focus();
  }
}

watch(
  trackedScrollY,
  (currentScrollY, previousScrollY = 0) => {
    isScrolled.value = currentScrollY > 10;

    if (!isMobile.value) {
      isGenresVisible.value = true;
      return;
    }

    const scrollDiff = currentScrollY - previousScrollY;

    if (Math.abs(scrollDiff) > scrollThreshold) {
      isGenresVisible.value = !(scrollDiff > 0 && currentScrollY > 100);
    }

    if (currentScrollY < 50) {
      isGenresVisible.value = true;
    }
  },
  { immediate: true },
);

onMounted(() => {
  window.addEventListener("keydown", handleKeydown);
  // Theme initialization is handled by App.vue via uiStore.initializeTheme()
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeydown);
  debouncedFetchSuggestions.cancel();
});

const emit = defineEmits<{
  "toggle-drawer": [];
  "open-search": [];
  "open-settings": [];
  "open-discover": [];
  "open-terms": [];
  "open-privacy": [];
}>();
</script>

<style scoped lang="scss">
.app-header {
  // Define safe area inset as CSS variable for consistency
  --safe-area-top: env(safe-area-inset-top, 0px);

  position: fixed;
  top: 0;
  // Use left/right: auto to let the header be positioned by its parent container
  // The container (app-container) is centered and has max-width
  left: 0;
  right: 0;
  z-index: var(--z-app-header);
  height: calc(64px + var(--safe-area-top)); // Account for notch/Dynamic Island
  padding-top: var(--safe-area-top); // Push content below notch/Dynamic Island
  background: rgba(12, 10, 9, 0.35);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  // border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: all 0.3s var(--ease-out-expo);

  // Desktop (lg+): pin to center column edges (see --app-header-* on .v-application in App.vue).
  @media (min-width: 1280px) {
    &:not(.app-header--mobile) {
      left: var(--app-header-offset-left);
      right: var(--app-header-offset-right, 0px);
      width: auto;
      max-width: var(--app-center-span);
      transform: none;
      overflow-x: clip;
    }
  }

  // iOS PWA / standalone mode - ensure proper safe area handling
  @supports (padding-top: env(safe-area-inset-top)) {
    @media (display-mode: standalone) {
      --safe-area-top: env(safe-area-inset-top, 47px);
    }
  }

  &--scrolled {
    background: rgba(12, 10, 9, 0.4);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.08),
      0 4px 24px rgba(0, 0, 0, 0.15);
  }

  /** Taller header only when tab strip is visible (after committed search) */
  &--search-tabs-expanded {
    height: auto;
    min-height: calc(64px + var(--safe-area-top));

    .app-header__inner {
      height: auto;
      min-height: 64px;
      align-items: flex-start;
      padding-top: 4px;
      padding-bottom: 2px;
    }
  }

  &--search-tabs-expanded.app-header--mobile {
    min-height: calc(56px + var(--safe-area-top));

    .app-header__inner {
      min-height: 56px;
    }
  }

  // Hide-on-scroll behavior for mobile
  &--hidden {
    transform: translateY(-100%);
    pointer-events: none;
  }

  // Simplified mobile header (with concave corner bites at bottom-left and bottom-right)
  &--mobile {
    height: calc(56px + var(--safe-area-top)); // Slightly smaller on mobile

    // Right corner: concave bite at bottom-right (transparent quarter shows content below)
    &::before {
      content: "";
      position: absolute;
      bottom: -20px;
      right: 0;
      width: 20px;
      height: 20px;
      background: radial-gradient(
        circle at 0 100%,
        transparent 0,
        transparent 20px,
        rgba(12, 10, 9, 0.35) 20px
      );
      backdrop-filter: blur(24px) saturate(180%);
      -webkit-backdrop-filter: blur(24px) saturate(180%);
      // box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
      pointer-events: none;
    }

    // Left corner: concave bite at bottom-left (transparent quarter shows content below)
    &::after {
      content: "";
      position: absolute;
      bottom: -20px;
      left: 0;
      width: 20px;
      height: 20px;
      background: radial-gradient(
        circle at 100% 100%,
        transparent 0,
        transparent 20px,
        rgba(12, 10, 9, 0.35) 20px
      );
      backdrop-filter: blur(24px) saturate(180%);
      -webkit-backdrop-filter: blur(24px) saturate(180%);
      // box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
      pointer-events: none;
    }

    .app-header__inner {
      height: 56px;
      padding: 0 12px;
    }

    // Must come after the fixed mobile heights above: search tab strip is a second row
    // inside __inner; without height:auto the strip stays in the DOM but is clipped.
    &.app-header--search-tabs-expanded {
      height: auto;
      min-height: calc(56px + var(--safe-area-top));

      .app-header__inner {
        height: auto;
        min-height: 56px;
        align-items: flex-start;
        padding-top: 6px;
        padding-bottom: 4px;
      }
    }

    // Logo on mobile - optimized for visibility and touch targets
    .app-header__logo {
      gap: 8px;
    }

    .app-header__logo-icon {
      width: 48px;
      height: 48px;
    }

    .app-header__logo-img {
      height: 48px; // Optimized for mobile app best practices (48px touch target standard)
      max-width: 200px; // Slightly increased max-width to accommodate larger logos
    }

    .app-header__logo-text {
      font-size: 1.125rem;
    }

    // Hide search bar on mobile (use bottom tab bar search)
    .app-header__search {
      display: none !important;
    }

    // Compact menu button
    .app-header__menu-btn {
      width: 36px;
      height: 36px;
    }
  }

  &__inner {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 64px; // Fixed inner height, padding-top handles safe area
    padding: 0 16px; // Add horizontal padding for content
    width: 100%;
    // No max-width needed - header width is constrained by .app-header

    /** Search: tabs nav is a sibling after shell + actions — wrap so tabs row is full inner width */
    &--search-tab-wrap {
      flex-wrap: wrap;
      align-content: flex-start;
      align-items: flex-start;
      row-gap: 4px;
      column-gap: 16px;
    }
  }

  // Menu button (mobile)
  &__menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    background: none;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    border-radius: 10px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__menu-logo-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    /* Solid mark (SVG currentColor); not menu-btn muted on-surface gray */
    color: #000000;
    opacity: 1;

    .v-theme--dark & {
      color: #ffffff;
    }

    :deep(svg) {
      display: block;
      width: 22px;
      height: 22px;
      flex-shrink: 0;
      color: inherit;
      opacity: 1;
    }
  }

  // Logo
  &__logo {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    flex-shrink: 0;
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }
  }

  &__logo-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: rgb(var(--v-theme-primary));
    border-radius: 10px;
    color: rgb(var(--v-theme-on-primary, 255 255 255));
    box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.3);
    cursor: pointer;
  }

  &__logo-img {
    height: 36px;
    width: auto;
    max-width: 180px;
    object-fit: contain;
    cursor: pointer;
  }

  &__logo-text {
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    cursor: pointer;
  }

  &__logo-cc {
    color: rgb(var(--v-theme-primary));
  }

  &__logo-demo {
    color: rgb(var(--v-theme-on-surface));
  }

  // Timeline Filter Bar (single-post mode, centered)
  &__timeline-filter {
    position: static;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 1;
    z-index: var(--z-inline-elevated);
    pointer-events: auto; // Ensure it's clickable
  }

  // Communities list hub (back + title; tabs in GroupsView — mirrors Discover)
  &__groups-page-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  &__groups-back {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
  }

  &__groups-title {
    flex: 0 1 auto;
    min-width: 0;
    margin: 0;
    font-family: var(--font-display, system-ui);
    font-size: 1.125rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgb(var(--v-theme-on-surface));
  }

  // Group feed bar (back + avatar + name + create)
  &__group-feed-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
    z-index: var(--z-inline-elevated);
    pointer-events: auto;
  }

  &__group-feed-back {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
  }

  &__group-feed-title {
    flex: 1;
    min-width: 0;
    margin-left: 8px;
    text-align: left;
    font-size: 1.125rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* ProfileScrollAppBar-style compact chip (primary/secondary gradient + overlay + ring avatar) */
  &__group-feed-identity {
    position: relative;
    display: flex;
    align-items: center;
    margin-left: 8px;
    border-radius: 8px;
    overflow: hidden;
    height: 48px;
    min-width: 0;
    flex: 1;
    max-width: min(100%, 320px);
  }

  &__group-feed-identity-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)) 0%,
      rgb(var(--v-theme-secondary)) 100%
    );
    background-size: cover;
    background-position: center;
  }

  &__group-feed-identity-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    pointer-events: none;
  }

  &__group-feed-identity-row {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    min-width: 0;
    flex: 1;
  }

  &__group-feed-identity-avatar {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.8);
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgb(var(--v-theme-surface));
    color: rgb(var(--v-theme-primary));

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__group-feed-identity-name {
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 600;
    color: #ffffff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    line-height: 1.2;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  }

  // Search page: shell = back + search only; tabs are &__inner child with flex-basis 100%
  &__search-page-shell {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    align-self: stretch;
  }

  &__search-page-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
    width: 100%;
  }

  &__search-page-tabs {
    display: flex;
    flex: 0 0 100%;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    justify-content: space-between;
    align-items: stretch;
    gap: 0;
    margin: 0;
    padding: 0;
    overflow-x: auto;
    scrollbar-width: none;
    // No full-width rule under inactive tabs — only `--active` uses ::after (primary).

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__search-page-tab {
    flex: 0 0 auto;
    position: relative;
    padding: 5px 10px 7px;
    border: none;
    background: none;
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 500;
    letter-spacing: 0.01em;
    color: rgba(var(--v-theme-on-surface), 0.55);
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.15s ease;

    &:hover {
      color: rgba(var(--v-theme-on-surface), 0.85);
    }

    &--active {
      color: rgb(var(--v-theme-on-surface));

      &::after {
        content: "";
        position: absolute;
        left: 6px;
        right: 6px;
        bottom: 0;
        height: 2px;
        border-radius: 2px 2px 0 0;
        background: rgb(var(--v-theme-primary));
      }
    }
  }

  // Discover hub (back + title + tabs, synced with route.query.tab)
  &__discover-page-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  &__discover-back {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
  }

  &__discover-title {
    flex: 0 1 auto;
    min-width: 0;
    margin: 0;
    font-family: var(--font-display, system-ui);
    font-size: 1.125rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgb(var(--v-theme-on-surface));
  }

  &__discover-toggle-wrap {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__discover-toggle {
    flex-shrink: 0;
    max-width: min(88vw, 400px);
  }

  &__discover-btn-inner {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  // Profile (back + title + settings or viewed user's avatar)
  &__profile-page-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  &__profile-back {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
  }

  &__profile-title {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    font-family: var(--font-display, system-ui);
    font-size: 1.125rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgb(var(--v-theme-on-surface));
  }

  &__profile-actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  &__profile-settings-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
  }

  &__profile-trailing-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background: rgb(var(--v-theme-surface));

    .v-img {
      width: 100%;
      height: 100%;
    }
  }

  &__profile-trailing-initial {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-family: var(--font-display, system-ui);
    font-size: 1rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
  }

  /* Initial inside group-feed-identity avatar (on cover / gradient) */
  &__profile-identity-initial {
    font-family: var(--font-body);
    font-size: 0.8125rem;
    font-weight: 700;
    color: #ffffff;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  }

  &__search-page-back {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
  }

  &__settings-page-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
  }

  &__settings-page-back {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
  }

  &__settings-page-title {
    flex: 1;
    min-width: 0;
    font-size: 1rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // SearchPageHeaderInput renders these classes inside a child component; scoped
  // CSS must pierce with :deep() or the pill never gets flex:1 (stays content-width).
  :deep(.app-header__search-page-input-wrap) {
    flex: 1;
    min-width: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 6px 12px;
    background: rgba(var(--v-theme-on-surface), 0.08);
    border-radius: 24px;
    border: 1px solid transparent;
  }

  :deep(.app-header__search-page-icon) {
    flex-shrink: 0;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }

  :deep(.app-header__search-page-input) {
    flex: 1;
    min-width: 0;
    border: none;
    background: none;
    outline: none;
    font-size: 0.9375rem;
    color: rgb(var(--v-theme-on-surface));

    &::placeholder {
      color: rgba(var(--v-theme-on-surface), 0.5);
    }
  }

  :deep(.app-header__search-page-clear) {
    flex-shrink: 0;
    padding: 0;
    border: none;
    background: none;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
  }

  &__group-feed-fav,
  &__group-feed-gear {
    flex-shrink: 0;
  }

  // Toggle group (All / Following) - smaller for header
  &__toggle-group {
    display: flex;
    background: rgba(var(--v-theme-on-surface), 0.06);
    border-radius: 20px;
    padding: 3px;
    gap: 2px;
  }

  &__toggle-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 8px;
    border: none;
    border-radius: 17px;
    background: transparent;
    color: rgba(var(--v-theme-on-surface), 0.6);
    font-family: var(--font-body);
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    gap: 4px;

    &:hover:not(:disabled):not(&--active) {
      color: rgba(var(--v-theme-on-surface), 0.8);
      background: rgba(var(--v-theme-on-surface), 0.04);
    }

    &--active {
      background: var(
        --explore-gradient,
        linear-gradient(
          135deg,
          rgb(var(--v-theme-primary)),
          rgb(var(--v-theme-secondary))
        )
      );
      color: rgb(var(--v-theme-on-primary, 255 255 255));
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(var(--v-theme-primary), 0.3);
    }

    &--disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  // Filter menu button (smaller for header)
  &__filter-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    padding: 1;
    background: transparent;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.12);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  // Filter menu dropdown
  &__filter-menu {
    min-width: 200px;
    padding: 8px 0;
    background: rgb(var(--v-theme-surface));
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  &__filter-menu-header {
    padding: 8px 16px 12px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }

  &__filter-menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    cursor: pointer;
    transition: background 0.15s ease;
    color: rgb(var(--v-theme-on-surface));
    font-size: 0.9375rem;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.05);
    }

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;

      &:hover {
        background: transparent;
      }
    }

    input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: rgb(var(--v-theme-primary));
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;
      }
    }

    .v-icon {
      color: rgba(var(--v-theme-on-surface), 0.6);
    }
  }

  // Search
  &__search {
    flex: 1;
    max-width: 600px;
    position: relative;
    flex-shrink: 1; // Allow it to shrink when filter is visible
  }

  &__search-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 42px;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    border-radius: 12px;
    transition: all 0.3s var(--ease-out-expo);

    &:focus-within {
      background: rgba(var(--v-theme-on-surface), 0.08);
      border-color: rgba(var(--v-theme-primary), 0.3);
      box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.1);
    }
  }

  &__search-icon {
    position: absolute;
    left: 14px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    pointer-events: none;
  }

  &__search-input {
    width: 100%;
    height: 100%;
    padding: 0 40px 0 42px;
    border: none;
    background: none;
    font-family: var(--font-body);
    font-size: 0.9375rem;
    color: rgb(var(--v-theme-on-surface));
    outline: none;

    &::placeholder {
      color: rgba(var(--v-theme-on-surface), 0.5);
    }
  }

  &__search-spinner {
    position: absolute;
    right: 12px;
  }

  // Suggestions dropdown
  &__suggestions {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: rgba(28, 25, 23, 0.98);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    overflow: hidden;
    z-index: var(--z-sticky-content);
  }

  &__suggestions-loading {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-size: 0.875rem;
  }

  &__suggestion {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 16px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover,
    &--active {
      background: rgba(var(--v-theme-on-surface), 0.05);
    }

    &--active {
      background: rgba(var(--v-theme-primary), 0.1);
    }
  }

  &__suggestion-icon {
    color: rgba(var(--v-theme-on-surface), 0.5);
    flex-shrink: 0;
  }

  &__suggestion-content {
    flex: 1;
    min-width: 0;
  }

  &__suggestion-text {
    display: block;
    font-size: 0.9375rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__suggestion-subtext {
    display: block;
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    margin-top: 2px;
  }

  &__suggestion-type {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.4);
    text-transform: capitalize;
    flex-shrink: 0;
  }

  // Actions
  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto; // Push to right edge
    flex-shrink: 0; // Prevent actions from shrinking
  }

  // Spacer to push content to the right (used in playlist edit mode)
  &__spacer {
    flex: 1;
  }

  &__action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    background: none;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    border-radius: 10px;
    transition: all 0.2s ease;
    text-decoration: none;

    &--single-post {
      width: 32px;
      height: 32px;
    }

    &--active {
      color: rgb(var(--v-theme-primary));
      background: rgba(var(--v-theme-primary), 0.1);
    }

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  // Feed toggle specific styling
  &__feed-toggle {
    position: relative;

    &::after {
      content: "";
      position: absolute;
      bottom: 2px;
      left: 50%;
      transform: translateX(-50%);
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: rgb(var(--v-theme-primary));
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &--active::after {
      opacity: 1;
    }

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
      color: rgb(var(--v-theme-on-surface));
    }

    &--active {
      color: rgb(var(--v-theme-primary));
      background: rgba(var(--v-theme-primary), 0.1);
    }
  }

  &__messages-btn {
    position: relative;
  }

  &__messages-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: rgb(var(--v-theme-primary));
    border-radius: 50px;
    font-size: 0.6875rem;
    font-weight: 700;
    color: rgb(var(--v-theme-on-primary, 12 10 9));
    border: 2px solid rgb(var(--v-theme-background));
  }

  &__queue-btn {
    position: relative;

    &--active {
      color: rgb(var(--v-theme-primary));
    }
  }

  &__queue-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: rgb(var(--v-theme-primary));
    border-radius: 50px;
    font-size: 0.6875rem;
    font-weight: 700;
    color: rgb(var(--v-theme-on-primary, 12 10 9));
    border: 2px solid rgb(var(--v-theme-background));
  }

  &__badge {
    :deep(.v-badge__badge) {
      font-size: 0.6875rem;
      font-weight: 600;
      min-width: 18px;
      height: 18px;
    }
  }

  // Avatar button
  &__avatar-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px;
    padding-right: 8px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.2s ease;

    .app-header--mobile & {
      padding-right: 4px;
      gap: 0;
    }

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.1);
    }
  }

  &__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)),
      rgb(var(--v-theme-secondary))
    );
    padding: 2px;

    :deep(.v-img) {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      overflow: hidden;
    }

    :deep(.v-responsive__sizer) {
      padding-bottom: 0 !important;
    }

    :deep(.v-img__img) {
      position: relative;
      width: 100%;
      height: 100%;
      border-radius: 50%;
    }
  }

  &__avatar-initial {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: rgba(var(--v-theme-on-surface), 0.08);
    border-radius: 50%;
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
  }

  &__avatar-caret {
    color: rgba(var(--v-theme-on-surface), 0.5);
    transition: transform 0.2s ease;

    .app-header--mobile & {
      display: none; // Prevent avatar overflow on mobile
    }
  }

  // Login button
  &__login-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    padding: 0 20px;
    background: rgb(var(--v-theme-primary));
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-primary, 255 255 255));
    text-decoration: none;
    transition: all 0.3s var(--ease-out-expo);
    box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.25);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(var(--v-theme-primary), 0.35);
    }
  }

  // Dropdown menu
  &__menu {
    min-width: 240px;
    padding: 8px;
    background: rgba(28, 25, 23, 0.98);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  }

  &__menu-user {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 12px;
    background: rgba(var(--v-theme-on-surface), 0.03);
  }

  &__menu-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)),
      rgb(var(--v-theme-secondary))
    );
    padding: 2px;
    flex-shrink: 0;

    .v-img,
    span {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: rgba(var(--v-theme-on-surface), 0.08);
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__menu-user-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__menu-name {
    font-weight: 600;
    font-size: 0.9375rem;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__menu-handle {
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }

  &__menu-divider {
    height: 1px;
    margin: 8px 4px;
    background: rgba(255, 255, 255, 0.06);
  }

  &__menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px;
    border: none;
    background: none;
    border-radius: 10px;
    font-family: var(--font-body);
    font-size: 0.9375rem;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
    transition: all 0.2s ease;

    .v-icon {
      color: inherit;
    }

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.06);
      color: rgb(var(--v-theme-on-surface));
    }

    &--danger {
      color: rgb(var(--v-theme-error));

      &:hover {
        background: rgba(var(--v-theme-error), 0.1);
        color: rgb(var(--v-theme-error));
      }
    }
  }
}

// Suggestions transition
.suggestions-enter-active,
.suggestions-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.suggestions-enter-from,
.suggestions-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

// Genre Pills
.app-header {
  &__genre-pills {
    display: flex;
    gap: 8px;
    padding: 8px 24px 12px;
    overflow-x: auto;
    scrollbar-width: none; // Firefox
    -ms-overflow-style: none; // IE/Edge
    background: rgba(12, 10, 9, 0.75);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    transition:
      transform 0.3s ease,
      opacity 0.3s ease;

    &::-webkit-scrollbar {
      display: none; // Chrome/Safari
    }

    &--hidden {
      transform: translateY(-100%);
      opacity: 0;
      pointer-events: none;
    }

    @media (min-width: 1400px) {
      max-width: 1400px;
      margin: 0 auto;
      padding: 8px 24px 12px;
    }
  }

  &__genre-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: linear-gradient(
      135deg,
      color-mix(in srgb, var(--genre-color) 20%, transparent),
      color-mix(in srgb, var(--genre-color) 8%, transparent)
    );
    border: 1px solid color-mix(in srgb, var(--genre-color) 25%, transparent);
    border-radius: 20px;
    text-decoration: none;
    white-space: nowrap;
    flex-shrink: 0;
    transition: all 0.2s ease;
    font-family: var(--font-body);
    font-size: 0.8125rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px
        color-mix(in srgb, var(--genre-color) 25%, transparent);
    }

    &:active {
      transform: scale(0.98);
    }

    .v-icon {
      color: var(--genre-color);
    }
  }
}

// Upload progress bar (sits below header)
.app-header {
  &__upload-bar {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: var(--z-inline-elevated);
    overflow: hidden;
  }

  &__upload-bar-track {
    height: 3px;
    width: 100%;
    background: rgba(var(--v-theme-on-surface), 0.1);

    .app-header__upload-bar--success & {
      background: rgba(34, 197, 94, 1);
      height: 3px;
    }

    .app-header__upload-bar--error & {
      background: rgba(239, 68, 68, 1);
      height: 3px;
    }
  }

  &__upload-bar-progress {
    height: 100%;
    background: rgb(var(--v-theme-primary));
    border-radius: 0 2px 2px 0;
    transition: width 0.2s ease-out;

    &--indeterminate {
      width: 30%;
      animation: upload-bar-slide 1.4s ease-in-out infinite;
    }
  }

  &__upload-bar-summary {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0;
    margin: 0;
    display: block;
    width: 100%;
    cursor: pointer;
    color: inherit;
    text-align: inherit;

    &:focus-visible {
      outline: 2px solid rgb(var(--v-theme-primary));
      outline-offset: 2px;
    }
  }

  &__upload-bar-chevron {
    transition: transform 0.2s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__upload-panel {
    position: relative;
    background: rgb(var(--v-theme-surface));
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.18);
    max-height: 320px;
    overflow-y: auto;
    z-index: 5;
  }

  &__upload-panel-item {
    padding: 10px 16px;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
    font-size: 0.78rem;

    &:last-child {
      border-bottom: 0;
    }

    &--error {
      color: rgb(239, 68, 68);
    }

    &--done {
      opacity: 0.7;
    }
  }

  &__upload-panel-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    &--meta {
      margin-top: 4px;
      color: rgba(var(--v-theme-on-surface), 0.65);
      font-size: 0.7rem;
    }
  }

  &__upload-panel-name {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }

  &__upload-panel-pct {
    flex: 0 0 auto;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }

  &__upload-panel-track {
    margin-top: 6px;
    height: 3px;
    width: 100%;
    background: rgba(var(--v-theme-on-surface), 0.1);
    border-radius: 2px;
    overflow: hidden;
  }

  &__upload-panel-track-fill {
    height: 100%;
    background: rgb(var(--v-theme-primary));
    transition: width 0.2s ease-out;

    .app-header__upload-panel-item--error & {
      background: rgb(239, 68, 68);
    }

    .app-header__upload-panel-item--done & {
      background: rgb(34, 197, 94);
    }
  }

  &__upload-panel-cancel {
    appearance: none;
    background: transparent;
    border: 0;
    color: rgb(var(--v-theme-primary));
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
    padding: 2px 6px;

    &:hover {
      text-decoration: underline;
    }
  }

  &__upload-bar-label {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 2px 0;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: rgb(var(--v-theme-on-surface));
    background: rgba(var(--v-theme-surface), 0.85);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);

    .app-header__upload-bar--success & {
      color: rgb(34, 197, 94);
    }

    .app-header__upload-bar--error & {
      color: rgb(239, 68, 68);
    }

    .v-icon {
      color: inherit;
    }

    span {
      white-space: nowrap;
    }
  }
}

@keyframes upload-bar-slide {
  0% {
    transform: translateX(-100%);
    width: 30%;
  }
  50% {
    width: 60%;
  }
  100% {
    transform: translateX(400%);
    width: 30%;
  }
}

.upload-indicator-enter-active,
.upload-indicator-leave-active {
  transition: all 0.3s ease;
}

.upload-indicator-enter-from,
.upload-indicator-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

.upload-panel-enter-active,
.upload-panel-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease,
    max-height 0.18s ease;
  overflow: hidden;
}

.upload-panel-enter-from,
.upload-panel-leave-to {
  opacity: 0;
  transform: translateY(-8px);
  max-height: 0;
}

// Light theme
.v-theme--light {
  .app-header {
    background: rgba(250, 250, 249, 0.8);
    border-bottom-color: rgba(0, 0, 0, 0.06);

    &--scrolled {
      background: rgba(250, 250, 249, 0.95);
      box-shadow: 0 4px 24px rgba(var(--v-theme-on-surface), 0.08);
    }

    &__logo-demo {
      color: rgb(var(--v-theme-on-surface));
    }

    &__search-wrapper {
      background: rgba(0, 0, 0, 0.04);
      border-color: rgba(var(--v-theme-on-surface), 0.08);

      &:focus-within {
        background: rgba(0, 0, 0, 0.06);
        border-color: rgba(var(--v-theme-primary), 0.4);
        box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.1);
      }
    }

    &__search-input {
      color: rgb(var(--v-theme-on-surface));

      &::placeholder {
        color: rgba(var(--v-theme-on-surface), 0.6);
      }
    }

    &__action-btn,
    &__menu-btn {
      color: rgba(var(--v-theme-on-surface), 0.4);

      &:hover {
        background: rgba(0, 0, 0, 0.06);
        color: rgb(var(--v-theme-on-surface));
      }
    }

    &__avatar-btn {
      background: rgba(0, 0, 0, 0.04);

      &:hover {
        background: rgba(var(--v-theme-on-surface), 0.08);
      }
    }

    &__menu {
      background: rgba(255, 255, 255, 0.98);
      border-color: rgba(var(--v-theme-on-surface), 0.08);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    }

    &__menu-user {
      background: rgba(var(--v-theme-on-surface), 0.03);
    }

    &__menu-name {
      color: rgb(var(--v-theme-on-surface));
    }

    &__menu-divider {
      background: rgba(0, 0, 0, 0.06);
    }

    &__menu-item {
      color: rgb(var(--v-theme-on-surface));

      .v-icon {
        color: inherit;
      }

      &:hover {
        background: rgba(0, 0, 0, 0.04);
        color: rgb(var(--v-theme-on-surface));
      }
    }

    &__suggestions {
      background: rgba(255, 255, 255, 0.98);
      border-color: rgba(var(--v-theme-on-surface), 0.1);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    }

    &__suggestion {
      &:hover,
      &--active {
        background: rgba(var(--v-theme-on-surface), 0.03);
      }

      &--active {
        background: rgba(var(--v-theme-primary), 0.08);
      }
    }

    &__suggestion-text {
      color: rgb(var(--v-theme-on-surface));
    }

    &__genre-pills {
      background: rgba(250, 250, 249, 0.8);
      border-bottom-color: rgba(0, 0, 0, 0.06);
    }

    &__genre-pill {
      color: rgb(var(--v-theme-on-surface));
    }
  }
}
</style>
